import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Preloader from 'components/Preloader';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import EmptyMenu from 'components/MenuNew/EmptyMenu';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { usePosPromotions } from 'pages/PosSettings/hooks/usePosPromotions';
import { MODE, MUI_TO_SX_STYLES } from 'pages/TableOrdering/config';
import CreateOrEditForm from 'pages/PosSettings/Promotions/CreateOrEditForm';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { HeaderContainer, StripedDataGrid } from 'pages/TableOrdering/styles';

const Promotions = () => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    handleCancel,
    formMethods,
    promotionColumns,
    promotionRows,
    loading,
    open,
    startCreate,
    mode,
    rowsPerPage,
    setRowsPerPage,
    productOptions,
    categoryOptions,
  } = usePosPromotions();

  return (
    <Box>
      <ConfirmPromptModal
        handleConfirm={formMethods.handleSubmit(handleSubmit)}
        submitData={formMethods.getValues()}
        hasUnsavedChanges={formMethods.formState.isDirty}
        formState={formMethods}
      />

      <HeaderContainer>
        <Button
          id="promotionsAdd"
          onClick={startCreate}
          variant="contained"
          color="primary"
          type="button"
          disabled={open}
          className={`${open ? 'disabledButton' : ''}`}
          startIcon={<AddOutlinedIcon />}
        >
          {t('Add Promotion')}
        </Button>
      </HeaderContainer>

      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
        PaperProps={{ sx: { maxHeight: '90vh', background: '#FAFAFA' } }}
      >
        <FormProvider {...formMethods}>
          <form
            noValidate
            onSubmit={formMethods.handleSubmit(handleSubmit)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            <DialogTitle sx={{ fontSize: '24px', fontWeight: 400, pr: 6 }}>
              {mode === MODE.CREATE
                ? t('Add Promotion')
                : t('Edit Promotion')}
              <IconButton
                aria-label="close"
                onClick={handleCancel}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  color: '#929398',
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
              <CreateOrEditForm
                productOptions={productOptions}
                categoryOptions={categoryOptions}
              />
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #E7E7E7', p: 2 }}>
              <Button variant="text" onClick={handleCancel}>
                {t('Cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                // Submitting an invalid form marks every problem instead of
                // leaving a dead button with nothing to explain it.
                disabled={loading || !formMethods.formState.isDirty}
              >
                {mode === MODE.CREATE
                  ? t('Add Promotion')
                  : t('table_ordering.save')}
              </LoadingButton>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>

      <StripedDataGrid
        sx={MUI_TO_SX_STYLES}
        autoHeight={true}
        hideFooterSelectedRowCount={true}
        isEditable={false}
        pagination
        paginationMode="client"
        pageSize={rowsPerPage}
        onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
        rowsPerPageOptions={[20, 50, 100]}
        pageSizeOptions={[20, 50, 100]}
        rows={promotionRows}
        columns={promotionColumns}
        components={{
          LoadingOverlay: Preloader,
          NoRowsOverlay: () => (
            <Box sx={{ mt: '100px' }}>
              <EmptyMenu text={t('There are no promotions yet')} />
            </Box>
          ),
        }}
        loading={loading}
      />
    </Box>
  );
};

export default Promotions;
